import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/landing/Logo";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const scrollTo = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
    } else if (window.location.pathname !== "/") {
      navigate(href === "#" ? "/" : "/" + href);
    } else if (href === "#") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-10 sm:mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            {/* Smaller on mobile so it doesn't overflow */}
            <div className="block sm:hidden mb-4">
              <Logo size={42} showWordmark />
            </div>
            <div className="hidden sm:block mb-4">
              <Logo size={64} showWordmark />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mb-6">
              Professional mobile auto detailing serving Maricopa, Arizona. 
              We bring showroom quality to your driveway.
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/reflections.details/" },
                { Icon: Facebook, href: "https://www.facebook.com/reflections.detailing.677206" },
              ].map(({ Icon, href }, i) => (
                <motion.a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-sky-400 hover:border-sky-500/30 transition-all"
                  whileHover={{ scale: 1.1, y: -2 }}
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-medium mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", href: "#" },
                { name: "Services", href: "/services" },
                { name: "Ceramic", href: "/ceramic" },
                { name: "Portfolio", href: "/portfolio" },
                { name: "Contact", href: "#contact" },
              ].map((link) => (
                <li key={link.name}>
                  <motion.button
                    onClick={() => scrollTo(link.href)}
                    className="text-gray-500 hover:text-sky-400 transition-colors text-sm"
                    whileHover={{ x: 3 }}
                  >
                    {link.name}
                  </motion.button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-medium mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href="tel:5203804407" className="flex items-center gap-2 text-gray-500 hover:text-sky-400 transition-colors text-sm">
                  <Phone className="w-4 h-4" />(520) 380-4407
                </a>
              </li>
              <li>
                <a href="mailto:Reflectionsautodetailingaz@gmail.com" className="flex items-center gap-2 text-gray-500 hover:text-sky-400 transition-colors text-sm">
                  <Mail className="w-4 h-4" />Email Us
                </a>
              </li>
              <li>
                <span className="flex items-center gap-2 text-gray-500 text-sm">
                  <MapPin className="w-4 h-4" />Maricopa, AZ
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-sm">© {currentYear} Reflections Auto Detailing</p>
          <div className="flex items-center gap-4">
            <p className="text-gray-600 text-sm">Serving Maricopa, Casa Grande, Chandler & More</p>
            <a href="/admin/login" data-testid="footer-admin" className="text-gray-700 hover:text-sky-500 text-xs uppercase tracking-widest transition-colors">
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
